import { useCallback, useMemo } from 'react';

import { MarketAccountStatusEnum, useMarketAccountsContext, useMarketsContext, type MarketAccount } from '@talos/kyoko';

import type { SourceDestinationOption } from '@src/containers/Transfers/types';
import type { CustomerBalanceTransactionDrawerState } from '../types';

interface UseTransferOptionsProps {
  drawerState: CustomerBalanceTransactionDrawerState;
}

export function useTransferOptions({ drawerState }: UseTransferOptionsProps) {
  const { marketAccountsList } = useMarketAccountsContext();
  const { marketsByName } = useMarketsContext();

  // Helper function to format labels exactly like the Addresses page
  const getTreasuryAccountDisplayValue = useCallback(
    (marketAccount: MarketAccount): string => {
      const market = marketsByName?.get(marketAccount.Market);
      const marketDisplayName = market?.DisplayName || market?.Name || marketAccount.Market;
      const marketAccountDisplayName = marketAccount.DisplayName || marketAccount.Name;
      return `${marketDisplayName} - ${marketAccountDisplayName}`;
    },
    [marketsByName]
  );

  // Convert market accounts to SourceDestinationOption format
  const sourceOptions = useMemo(() => {
    if (!marketAccountsList) {
      return [];
    }

    return marketAccountsList
      .filter(account => {
        // Filter by provider if selected
        if (drawerState.provider && account.Market !== drawerState.provider) {
          return false;
        }
        // Only include active accounts
        if (account.Status !== MarketAccountStatusEnum.Active) {
          return false;
        }
        return true;
      })
      .map(
        account =>
          ({
            marketAccount: account,
            label: getTreasuryAccountDisplayValue(account),
            addressForCurrency: drawerState.currency,
          }) as SourceDestinationOption
      );
  }, [marketAccountsList, getTreasuryAccountDisplayValue, drawerState.provider, drawerState.currency]);

  const destinationOptions = useMemo(() => {
    if (!marketAccountsList) {
      return [];
    }

    return marketAccountsList
      .filter(account => {
        // Filter by provider if selected
        if (drawerState.provider && account.Market !== drawerState.provider) {
          return false;
        }
        // Only include active accounts
        if (account.Status !== MarketAccountStatusEnum.Active) {
          return false;
        }
        return true;
      })
      .map(
        account =>
          ({
            marketAccount: account,
            label: getTreasuryAccountDisplayValue(account),
            addressForCurrency: drawerState.currency,
          }) as SourceDestinationOption
      );
  }, [marketAccountsList, getTreasuryAccountDisplayValue, drawerState.provider, drawerState.currency]);

  return {
    sourceOptions,
    destinationOptions,
    getTreasuryAccountDisplayValue,
  };
}
